import { AUTHKIT_URL } from "./authProvider";

export interface AuthkitUser {
  id: string;
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
}

export interface AuthkitSession {
  authenticated: true;
  user: AuthkitUser;
  organizationId: string | null;
  csrfToken: string;
  /**
   * Access-token expiry (ms epoch) from the meq_at `exp` claim, surfaced by
   * the worker so silent refresh (V3) can drive its skew check + expiry
   * timer without reading the HttpOnly cookie. Null if the worker didn't
   * include it (older build).
   */
  expiresAt: number | null;
}

export interface AuthkitRefreshResult {
  /** Expiry (ms epoch) of the rotated access token; null if not returned. */
  expiresAt: number | null;
  /**
   * Rotated session-bound CSRF token. CSRF tokens embed their issue
   * timestamp and expire after 15 min, so the worker mints a fresh one on
   * every successful refresh. Null if the worker didn't include it
   * (older build) — callers keep their current token in that case.
   */
  csrfToken: string | null;
}

export interface AuthkitLoginResult {
  user: AuthkitUser;
  organizationId: string | null;
}

export class AuthkitError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = "AuthkitError";
    this.status = status;
    this.code = code;
  }
}

interface LoginParams {
  email: string;
  password: string;
  organizationId?: string;
}

const FETCH_INIT: RequestInit = {
  credentials: "include",
  headers: { "Content-Type": "application/json" },
};

const url = (path: string) => `${AUTHKIT_URL}${path}`;

const networkError = (cause: unknown): AuthkitError => {
  const err = new AuthkitError(
    0,
    "Network request to authkit-ui failed",
    "network",
  );
  (err as { cause?: unknown }).cause = cause;
  return err;
};

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function extractErrorCode(body: unknown): string | undefined {
  if (typeof body === "object" && body !== null && "error" in body) {
    const code = (body as { error: unknown }).error;
    if (typeof code === "string") return code;
  }
  return undefined;
}

async function fetchBootstrapCsrf(): Promise<string> {
  let response: Response;
  try {
    response = await fetch(url("/api/csrf"), { credentials: "include" });
  } catch (cause) {
    throw networkError(cause);
  }
  if (!response.ok) {
    const body = await readJsonSafe(response);
    throw new AuthkitError(
      response.status,
      "Failed to issue bootstrap CSRF token",
      extractErrorCode(body),
    );
  }
  const body = (await readJsonSafe(response)) as { csrfToken?: unknown } | null;
  if (!body || typeof body.csrfToken !== "string") {
    throw new AuthkitError(
      response.status,
      "Bootstrap CSRF response missing csrfToken",
      "invalid_response",
    );
  }
  return body.csrfToken;
}

/**
 * Cross-origin password authentication via authkit-ui /api/login.
 * Fetches a bootstrap CSRF token first, then POSTs the credentials.
 * On 200 the response carries { user, organizationId } and the browser
 * holds meq_at + meq_rt cookies.
 */
export async function postLogin(
  params: LoginParams,
): Promise<AuthkitLoginResult> {
  const csrfToken = await fetchBootstrapCsrf();
  let response: Response;
  try {
    response = await fetch(url("/api/login"), {
      ...FETCH_INIT,
      method: "POST",
      headers: { ...FETCH_INIT.headers, "X-CSRF-Token": csrfToken },
      body: JSON.stringify(params),
    });
  } catch (cause) {
    throw networkError(cause);
  }
  const body = (await readJsonSafe(response)) as
    | (Partial<AuthkitLoginResult> & { success?: boolean; error?: string })
    | null;

  if (!response.ok || !body || body.success === false) {
    throw new AuthkitError(
      response.status,
      "Login failed",
      extractErrorCode(body),
    );
  }
  if (!body.user) {
    throw new AuthkitError(
      response.status,
      "Login response missing user",
      "invalid_response",
    );
  }
  return {
    user: body.user,
    organizationId: body.organizationId ?? null,
  };
}

/**
 * Read the current authkit-ui session via /api/session. Returns the
 * hydrated session when authenticated, or null when the response says
 * unauthenticated (cookie missing/expired). Throws AuthkitError on
 * non-2xx or network failure.
 */
export async function getSession(): Promise<AuthkitSession | null> {
  let response: Response;
  try {
    response = await fetch(url("/api/session"), { credentials: "include" });
  } catch (cause) {
    throw networkError(cause);
  }
  if (response.status === 401) {
    return null;
  }
  const body = await readJsonSafe(response);
  if (!response.ok) {
    throw new AuthkitError(
      response.status,
      "Session request failed",
      extractErrorCode(body),
    );
  }
  const data = body as Partial<AuthkitSession> & { authenticated?: boolean };
  if (!data?.authenticated) {
    return null;
  }
  if (!data.user || typeof data.csrfToken !== "string") {
    throw new AuthkitError(
      response.status,
      "Session response missing user or csrfToken",
      "invalid_response",
    );
  }
  return {
    authenticated: true,
    user: data.user,
    organizationId: data.organizationId ?? null,
    csrfToken: data.csrfToken,
    expiresAt: data.expiresAt ?? null,
  };
}

/**
 * Rotate the WorkOS session via authkit-ui /api/refresh. Requires the
 * session-bound csrfToken from a prior getSession(). On 200 the browser
 * holds rotated meq_at + meq_rt cookies and the response carries the new
 * access token's expiry. Throws AuthkitError on 401 (dead/rotated refresh
 * token — caller should drop to login), 403 (bad CSRF), or network failure.
 */
export async function postRefresh(
  csrfToken: string,
): Promise<AuthkitRefreshResult> {
  let response: Response;
  try {
    response = await fetch(url("/api/refresh"), {
      ...FETCH_INIT,
      method: "POST",
      headers: { ...FETCH_INIT.headers, "X-CSRF-Token": csrfToken },
    });
  } catch (cause) {
    throw networkError(cause);
  }
  const body = await readJsonSafe(response);
  if (!response.ok) {
    throw new AuthkitError(
      response.status,
      "Refresh failed",
      extractErrorCode(body),
    );
  }
  const data = body as {
    expiresAt?: number | null;
    csrfToken?: string | null;
  } | null;
  return {
    expiresAt: data?.expiresAt ?? null,
    csrfToken: typeof data?.csrfToken === "string" ? data.csrfToken : null,
  };
}

/**
 * Terminate the WorkOS session via authkit-ui /api/logout. Requires the
 * session-bound csrfToken obtained from a prior getSession() call.
 */
export async function postLogout(csrfToken: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(url("/api/logout"), {
      ...FETCH_INIT,
      method: "POST",
      headers: { ...FETCH_INIT.headers, "X-CSRF-Token": csrfToken },
    });
  } catch (cause) {
    throw networkError(cause);
  }
  if (!response.ok) {
    const body = await readJsonSafe(response);
    throw new AuthkitError(
      response.status,
      "Logout failed",
      extractErrorCode(body),
    );
  }
}
